import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Select,
  Icon,
  Upload,
  message,
  Typography,
  Row,
  Col,
  Alert,
  Collapse,
  Tag,
  Radio,
  Divider,
} from 'antd';
import { getToken } from './utils/authService';
import { getServer, getServerType, getServerDomain, setServer } from './utils/serverConfig';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * Truncate response data for storage to prevent localStorage quota issues
 * Keeps essential info but limits the size of stored data
 */
const truncateResponseForStorage = (responseInfo) => {
  const MAX_DATA_SIZE = 50000; // 50KB max for response data
  
  if (!responseInfo || !responseInfo.data) {
    return responseInfo;
  }

  const dataStr = JSON.stringify(responseInfo.data);
  
  // If data is small enough, keep it as is
  if (dataStr.length <= MAX_DATA_SIZE) {
    return responseInfo;
  }

  // Otherwise, create a truncated version
  const truncatedData = {
    _truncated: true,
    _originalSize: dataStr.length,
    _truncatedSize: MAX_DATA_SIZE,
    _preview: dataStr.substring(0, MAX_DATA_SIZE),
    _note: 'Response data was truncated for storage. Full response is not saved in history.',
  };

  return {
    ...responseInfo,
    data: truncatedData,
  };
};

const ApiTester = () => {
  const [method, setMethod] = useState('POST');
  const [endpoint, setEndpoint] = useState('/fuel/mileage-report');
  const [payload, setPayload] = useState('{\n  "asset": null,\n  "location": "all",\n  "subLocation": "all",\n  "periodFrom": "2025-10-31T18:30:00.000Z",\n  "periodTo": "2025-12-01T18:29:59.999Z"\n}');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [serverType, setServerType] = useState(getServerType());
  const [requestHistory, setRequestHistory] = useState([]);
  const [hasToken, setHasToken] = useState(!!getToken());
  const [bodyType, setBodyType] = useState('json');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    // Load request history from localStorage
    const saved = localStorage.getItem('api_tester_history');
    if (saved) {
      try {
        setRequestHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading request history:', e);
      }
    }
    
    // Check for token on mount and set up interval to check for changes
    const checkToken = () => {
      setHasToken(!!getToken());
    };
    
    checkToken();
    const interval = setInterval(checkToken, 1000); // Check every second
    
    return () => clearInterval(interval);
  }, []);

  const handleServerChange = (e) => {
    const newServerType = e.target.value;
    setServer(newServerType);
    setServerType(newServerType);
    message.info(`Switched to ${newServerType === 'production' ? 'Production' : 'Demo'} server`);
  };

  const handleTestApi = async () => {
    if (!endpoint || !endpoint.trim()) {
      message.error('Please enter an API endpoint');
      return;
    }

    const token = getToken();
    
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const apiBaseUrl = getServer();
      const url = endpoint.startsWith('http') ? endpoint : `${apiBaseUrl}${endpoint}`;
      
      let requestBody = null;
      if (method !== 'GET' && bodyType === 'json' && payload) {
        try {
          requestBody = JSON.parse(payload);
        } catch (e) {
          message.error('Invalid JSON payload');
          setLoading(false);
          return;
        }
      }

      // Always include token if available, but don't block request if it's not
      const headers = {
        'accept': 'application/json',
        'X-Tenant-Id': 'newa',
        'X-Api-Version': '2.0',
      };

      if (bodyType === 'json' && method !== 'GET') {
        headers['Content-Type'] = 'application/json';
      }

      // Add Authorization header if token is available (NO "Bearer" prefix as per API requirements)
      if (token) {
        headers['Authorization'] = token; // No "Bearer " prefix
        console.log('Token found, including in request (without Bearer prefix)');
      } else {
        console.warn('No token found - request will be made without authentication');
        message.warning('No authentication token found. Request will be made without Authorization header.', 3);
      }

      const requestOptions = {
        method: method,
        headers: headers,
      };

      if (bodyType === 'multipart' && method !== 'GET') {
        const formData = new FormData();

        if (payload && payload.trim()) {
          let multipartFields;
          try {
            multipartFields = JSON.parse(payload);
          } catch (e) {
            message.error('Invalid JSON payload for multipart fields');
            setLoading(false);
            return;
          }

          Object.entries(multipartFields).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (Array.isArray(value) || typeof value === 'object') {
              formData.append(key, JSON.stringify(value));
              return;
            }
            formData.append(key, String(value));
          });
        }

        if (selectedFile) {
          formData.append('file', selectedFile);
        }

        requestOptions.body = formData;
        delete requestOptions.headers['Content-Type'];
      } else if (requestBody) {
        requestOptions.body = JSON.stringify(requestBody);
      }

      console.log('=== API TEST REQUEST ===');
      console.log('Method:', method);
      console.log('URL:', url);
      console.log('Headers:', headers);
      console.log('Body:', requestBody);

      const startTime = Date.now();
      const fetchResponse = await fetch(url, requestOptions);
      const endTime = Date.now();
      const duration = endTime - startTime;

      console.log('Response status:', fetchResponse.status);
      console.log('Response headers:', Object.fromEntries(fetchResponse.headers.entries()));

      const contentType = fetchResponse.headers.get('content-type');
      let responseData;

      if (contentType && contentType.includes('application/json')) {
        responseData = await fetchResponse.json();
      } else {
        const text = await fetchResponse.text();
        responseData = { _rawText: text, _note: 'Response was not JSON' };
      }

      const responseInfo = {
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        ok: fetchResponse.ok,
        headers: Object.fromEntries(fetchResponse.headers.entries()),
        data: responseData,
        duration: duration,
        timestamp: new Date().toISOString(),
      };

      console.log('=== API TEST RESPONSE ===');
      console.log('Full response:', responseInfo);

      setResponse(responseInfo);

      // Save to history (with size limits to prevent quota exceeded errors)
      const historyItem = {
        method,
        endpoint,
        bodyType,
        fileName: selectedFile?.name || null,
        payload: requestBody,
        response: truncateResponseForStorage(responseInfo),
        timestamp: new Date().toISOString(),
      };

      // Keep only last 5 items to prevent localStorage quota issues
      const newHistory = [historyItem, ...requestHistory.slice(0, 4)]; // Keep last 5
      setRequestHistory(newHistory);
      
      // Try to save to localStorage, but handle quota errors gracefully
      try {
        const historyJson = JSON.stringify(newHistory);
        // Check size (localStorage limit is usually 5-10MB)
        if (historyJson.length > 2 * 1024 * 1024) { // 2MB limit
          console.warn('History too large, keeping only last 3 items');
          const reducedHistory = [historyItem, ...requestHistory.slice(0, 2)];
          localStorage.setItem('api_tester_history', JSON.stringify(reducedHistory));
          setRequestHistory(reducedHistory);
        } else {
          localStorage.setItem('api_tester_history', JSON.stringify(newHistory));
        }
      } catch (e) {
        if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
          console.warn('localStorage quota exceeded, clearing old history and keeping only current item');
          // Clear old history and keep only the current item
          const minimalHistory = [historyItem];
          try {
            localStorage.setItem('api_tester_history', JSON.stringify(minimalHistory));
            setRequestHistory(minimalHistory);
            message.warning('History was too large. Old history cleared. Only current request saved.');
          } catch (e2) {
            // Even single item is too large, don't save history
            console.error('Cannot save history even with single item:', e2);
            localStorage.removeItem('api_tester_history');
            message.warning('Response too large to save in history. History disabled.');
          }
        } else {
          console.error('Error saving history:', e);
          message.warning('Could not save request to history');
        }
      }

      if (fetchResponse.ok) {
        message.success(`API call successful! (${duration}ms)`);
      } else {
        message.warning(`API call completed with status ${fetchResponse.status}`);
      }
    } catch (err) {
      console.error('API test error:', err);
      setError({
        message: err.message,
        stack: err.stack,
        name: err.name,
      });
      message.error(`API call failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFromHistory = (item) => {
    setMethod(item.method);
    setEndpoint(item.endpoint);
    setBodyType(item.bodyType || 'json');
    setSelectedFile(null);
    if (item.payload) {
      setPayload(JSON.stringify(item.payload, null, 2));
    } else {
      setPayload('');
    }
    setResponse(item.response);
    message.info('Loaded from history');
  };

  const handleClearHistory = () => {
    setRequestHistory([]);
    localStorage.removeItem('api_tester_history');
    message.success('History cleared');
  };

  const loadApiExample = (apiName) => {
    const examples = {
      'mileage-report': {
        method: 'POST',
        endpoint: '/fuel/mileage-report',
        bodyType: 'json',
        payload: JSON.stringify({
          asset: null,
          location: 'all',
          subLocation: 'all',
          periodFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          periodTo: new Date().toISOString(),
        }, null, 2),
      },
      'send-email-with-attachment': {
        method: 'POST',
        endpoint: '/common/send-email-with-attachment',
        bodyType: 'multipart',
        payload: JSON.stringify({
          subject: 'AssetPro email API test',
          to: 'rishabhberia@gmail.com',
          body: 'This is a test email sent from the AssetPro API tester with a file attachment.'
        }, null, 2),
      },
    };

    const example = examples[apiName];
    if (example) {
      setMethod(example.method);
      setEndpoint(example.endpoint);
      setBodyType(example.bodyType || 'json');
      setPayload(example.payload);
      setSelectedFile(null);
      message.success(`Loaded ${apiName} example`);
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <Title level={3} style={{ marginBottom: 24 }}>
          API Tester
        </Title>

        {/* API Reference Quick Access */}
        <Card size="small" style={{ marginBottom: 16, background: '#e6f7ff', border: '1px solid #91d5ff' }}>
          <Row gutter={16} align="middle">
            <Col span={24} style={{ marginBottom: 8 }}>
              <Text strong>📚 API Reference: </Text>
              <Text>
                See <code>API_REFERENCE.md</code> in the project root for complete API documentation.
              </Text>
            </Col>
            <Col span={24}>
              <Text strong style={{ marginRight: 8 }}>Quick Examples:</Text>
              <Button 
                size="small" 
                type="link" 
                onClick={() => loadApiExample('mileage-report')}
                style={{ padding: 0, marginRight: 8 }}
              >
                Mileage Report
              </Button>
              <Button 
                size="small" 
                type="link" 
                onClick={() => loadApiExample('send-email-with-attachment')}
                style={{ padding: 0, marginRight: 8 }}
              >
                Email With Attachment
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Server Selection */}
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>Server: </Text>
            </Col>
            <Col>
              <Radio.Group value={serverType} onChange={handleServerChange} buttonStyle="solid">
                <Radio.Button value="demo">
                  <span>Demo</span>
                </Radio.Button>
                <Radio.Button value="production">
                  <span>Production</span>
                </Radio.Button>
              </Radio.Group>
            </Col>
            <Col>
              <Tag color={serverType === 'production' ? 'red' : 'blue'}>
                {getServerDomain()}
              </Tag>
            </Col>
            <Col>
              <Tag color={hasToken ? 'green' : 'orange'}>
                {hasToken ? '✓ Authenticated' : '⚠ No Token'}
              </Tag>
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Base URL: {getServer()}
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Request Configuration */}
        <Card title="Request Configuration" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Method:</Text>
                <Select
                  value={method}
                  onChange={setMethod}
                  style={{ width: '100%', marginTop: 8 }}
                >
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="DELETE">DELETE</Option>
                  <Option value="PATCH">PATCH</Option>
                </Select>
              </div>
            </Col>
            <Col span={18}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Endpoint:</Text>
                <Input
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="/fuel/mileage-report"
                  style={{ marginTop: 8 }}
                  addonBefore={getServer()}
                />
              </div>
            </Col>
          </Row>

          {(method === 'POST' || method === 'PUT' || method === 'PATCH') && (
            <>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Body Type:</Text>
                <Radio.Group
                  value={bodyType}
                  onChange={(e) => setBodyType(e.target.value)}
                  style={{ marginTop: 8, display: 'block' }}
                >
                  <Radio.Button value="json">JSON</Radio.Button>
                  <Radio.Button value="multipart">Multipart Form</Radio.Button>
                </Radio.Group>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>{bodyType === 'multipart' ? 'Form Fields (JSON)' : 'Payload (JSON)'}:</Text>
                <TextArea
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  placeholder={bodyType === 'multipart' ? '{"subject":"...", "to":"...", "body":"..."}' : '{"key": "value"}'}
                  rows={8}
                  style={{ marginTop: 8, fontFamily: 'monospace' }}
                />
              </div>

              {bodyType === 'multipart' && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Attachment:</Text>
                  <Divider style={{ margin: '8px 0 12px' }} />
                  <Upload
                    beforeUpload={(file) => {
                      setSelectedFile(file);
                      return false;
                    }}
                    onRemove={() => {
                      setSelectedFile(null);
                    }}
                    fileList={selectedFile ? [selectedFile] : []}
                    maxCount={1}
                  >
                    <Button>
                      <Icon type="upload" /> Choose File
                    </Button>
                  </Upload>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    The API expects the file field name to be <code>file</code>.
                  </Text>
                </div>
              )}
            </>
          )}

          <Button
            type="primary"
            onClick={handleTestApi}
            loading={loading}
            size="large"
            icon="rocket"
            block
          >
            {loading ? 'Sending Request...' : 'Send Request'}
          </Button>
        </Card>

        {/* Response Display */}
        {error && (
          <Card title="Error" style={{ marginBottom: 16 }}>
            <Alert
              message="Request Failed"
              description={error.message}
              type="error"
              showIcon
            />
            {error.stack && (
              <pre style={{ marginTop: 16, background: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 11 }}>
                {error.stack}
              </pre>
            )}
          </Card>
        )}

        {response && (
          <Card
            title={
              <span>
                Response{' '}
                <Tag color={response.ok ? 'green' : 'red'}>
                  {response.status} {response.statusText}
                </Tag>
                <Tag>{response.duration}ms</Tag>
                {response.data && response.data._truncated && (
                  <Tag color="orange" style={{ marginLeft: 8 }}>
                    ⚠ Truncated in History
                  </Tag>
                )}
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>Status: </Text>
                <Tag color={response.ok ? 'green' : 'red'}>
                  {response.status} {response.statusText}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong>Duration: </Text>
                <Text>{response.duration}ms</Text>
              </Col>
            </Row>

            <Collapse defaultActiveKey={['data']}>
              <Panel header="Response Data" key="data">
                {response.data && response.data._truncated && (
                  <Alert
                    message="Truncated Data"
                    description={`This response was truncated for storage. Original size: ${(response.data._originalSize / 1024).toFixed(2)}KB. Only ${(response.data._truncatedSize / 1024).toFixed(2)}KB is saved in history.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 500,
                    border: '1px solid #d9d9d9',
                  }}
                >
                  {JSON.stringify(response.data, null, 2)}
                </pre>
              </Panel>
              <Panel header="Response Headers" key="headers">
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 300,
                  }}
                >
                  {JSON.stringify(response.headers, null, 2)}
                </pre>
              </Panel>
            </Collapse>
          </Card>
        )}

        {/* Request History */}
        {requestHistory.length > 0 && (
          <Card
            title={
              <span>
                Request History{' '}
                <Button
                  type="link"
                  size="small"
                  onClick={handleClearHistory}
                  style={{ padding: 0, marginLeft: 8 }}
                >
                  Clear
                </Button>
              </span>
            }
            size="small"
          >
            {requestHistory.map((item, index) => (
              <div
                key={index}
                style={{
                  padding: 8,
                  marginBottom: 8,
                  background: '#fafafa',
                  borderRadius: 4,
                  border: '1px solid #e8e8e8',
                  cursor: 'pointer',
                }}
                onClick={() => handleLoadFromHistory(item)}
              >
                <Row gutter={16} align="middle">
                  <Col span={2}>
                    <Tag color={item.method === 'GET' ? 'blue' : item.method === 'POST' ? 'green' : 'orange'}>
                      {item.method}
                    </Tag>
                  </Col>
                  <Col span={18}>
                    <Text code style={{ fontSize: 12 }}>
                      {item.endpoint}{item.bodyType === 'multipart' ? ' [multipart]' : ''}
                    </Text>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    <Tag color={item.response.ok ? 'green' : 'red'}>
                      {item.response.status}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </Text>
                  </Col>
                </Row>
              </div>
            ))}
          </Card>
        )}
      </Card>
    </div>
  );
};

export default ApiTester;
